<template>
	<div class="videos-wrapper panel--full-height">
		<FilterableList
			:title="filterableListTitle"
			:is-filter="false"
			modifier="videos"
		>
			<template #list-view>
				<div
					v-if="isLoading"
					class="loader"
				>
					<LoadingSpinner
						center
						:theme="$arenaConfig.isLightTheme ? 'light' : 'dark'"
						:delay="0"
					/>
				</div>
				<ListView
					:items="filteredVideos"
					:swipable="false"
					:enable-empty-msg="initialDataLoaded"
				>
					<template
						v-if="initialDataLoaded"
						#no-results
					>
						<div class="no-results">
							<Icon
								icon-name="video-playlist"
								size="lg"
							/>
							{{ noResultsMsg }}
						</div>
						<EventBannerAd />
					</template>
					<template
						v-if="initialDataLoaded"
						#before-items
					>
						<EventBannerAd />
					</template>
					<template #item-row="{ item }">
						<VideoRow
							:video="item"
							:event="event"
							:sport-name="event.sportName"
							@play="handlePlay"
							@share="handleShare"
						/>
					</template>
				</ListView>
			</template>
		</FilterableList>
		<ShareModal
			v-if="isShareModalOpen"
			:title="shareModalTitle"
			:item="shareItem"
			:url-label="shareUrlLabel"
			:url="shareUrl"
			:links="shareLinks"
			@close="closeShareModal"
		/>
	</div>
</template>

<script>
import VideoRow from './VideoRow';
import shareVideo from '@videotools/js/mixins/shareVideo';
import { LoadingSpinner } from '@components/loader';
import ShareModal from '@components/share-modal';
import { SORT_KEYS } from '@videotools/js/constants/api';
import FilterableList from '@components/lists/FilterableList';
import ListView from '@components/lists/ListView';
import { SportName } from '@shared/enums/SportName';
import EventBannerAd from '@components/EventBannerAd';
import { mapActions, mapGetters } from 'vuex';
import laroute from '@laroute';

export default {
	name:       'Videos',
	components: {
		VideoRow,
		ListView,
		FilterableList,
		ShareModal,
		LoadingSpinner,
		EventBannerAd,
	},
	mixins: [shareVideo],
	props:  {
	},
	data() {
		return {
		  	isLoading: false,
		  	sortDir:   SORT_KEYS.NEWEST_FIRST,
		};
	},
	computed: {
		...mapGetters({
			event:               'event/event',
			isPlayerCompetition: 'event/isPlayerCompetition',
			isTouchDevice:       'device/isTouchDevice',
			supportsNativeShare: 'device/supportsNativeShare',
			sortKey:             'videos/sortKey',
			videos:              'videos/videos',
			isLastPage:          'videos/isLastPage',
			initialDataLoaded:   'videos/initialDataLoaded',
		}),
		filterableListTitle() {
			return this.hasLiveEvents ? this.$I18n.trans_choice('commons.events_and_videos') : this.$I18n.trans_choice('commons.video', 2);
		},
		filteredVideos() {
			return this.videos.filter(video => !(video.isLive && this.isVolleyball));
		},
		isVolleyball() {
			return this.event?.sport?.name === SportName.VOLLEYBALL;
		},
		hasLiveEvents() {
			return this.filteredVideos.filter((event) => event.isLive).length;
		},
		sortOptions() {
			return [
				{
					id:      SORT_KEYS.NEWEST_FIRST,
					label:   this.transUcfirst('commons.newest_first'),
					icon:    'sort-tool',
					handler: this.handleSortNewest,
				},
				{
					id:      SORT_KEYS.OLDEST_FIRST,
					label:   this.transUcfirst('commons.oldest_first'),
					icon:    'sort-tool',
					handler: this.handleSortOldest,
				},
				{
					id:      SORT_KEYS.VIEWS,
					label:   this.$Helpers.ucfirst(this.$I18n.trans_choice('commons.view', 2)),
					icon:    'sort-tool',
					handler: this.handleSortViews,
				},
		  ];
		},
		noResultsMsg() {
			return this.trans('commons.no_videos_and_events');
		},
	},
	mounted() {
		// TODO: quick fix to prevent the videos list from being empty
		if (!this.videos.length) {
		  this.init();
		}
	},
	methods: {
		...mapActions({
			changeSortKey: 'videos/changeSortKey',
			resetState:    'videos/resetState',
			loadVideos:    'videos/loadVideos',
			loadMore:      'videos/loadMore',
			shareNative:   'device/shareNative',
		}),
		handleShare(item) {
			item.shareLinks = {
				facebook: `https://www.facebook.com/sharer/sharer.php?u=${item.url}`,
				twitter:  `https://twitter.com/intent/tweet?text=${item.url}`,
				whatsapp: `https://web.whatsapp.com/send?text=${this.url}`,
				mail:     `mailto:?body=${item.url}`,
			};
			if (this.isTouchDevice && this.supportsNativeShare) {
				this.shareNative({
					url: item.url,
				});
			} else {
				this.openShareModal(item);
			}
		},
		handlePlay(video) {
			const slug = this.$Helpers.slugify(video.event?.nameTranslations?.[this.$arenaConfig.defaultLanguage] || video.nameTranslations?.[this.$arenaConfig.defaultLanguage] || video.name);
			window.location.href = laroute.route('arena.event', {
				event: video.id,
				slug:  slug,
			});
		},
		handleSortNewest() {
			this.handleSort(SORT_KEYS.NEWEST_FIRST);
		},
		handleSortOldest() {
			this.handleSort(SORT_KEYS.OLDEST_FIRST);
		},
		handleSortViews() {
			this.handleSort(SORT_KEYS.VIEWS);
		},
		handleSort(dir) {
			this.sortDir = dir;
			this.changeSortKey(this.sortDir);
			this.loadVideos();
		},
		async init() {
			try {
				this.isLoading = true;
				await this.loadVideos();
			} finally {
				this.isLoading = false;
			}
		},
	},
};
</script>

<style lang="scss" scoped>
@import '@shared/sass/shared-variables';

// .videos-wrapper {}

.no-results {
	display: flex;
	padding-top: 36px;
	padding-left: 8px;

	.v2-icon {
		margin-right: 18px;
	}
}

.mobile .loader {
	height: calc(100% - 134px);
}

.loader {
	padding: 24px 0;
}
</style>
